import React, { useState, useRef, useImperativeHandle, forwardRef } from "react";
import {
  TextField,
  Popover,
  Box,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { ChevronLeft, ChevronRight, CalendarToday } from "@mui/icons-material";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const parseLocalDate = (str) => {
  if (!str) return null;
  const [y, m, d] = str.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const toDateString = (date) => {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const toDisplayString = (date) => {
  if (!date) return "";
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  const y = date.getFullYear();
  return `${m}/${d}/${y}`;
};

// Strip color shared between start-half, in-range, and end-half cells
const RANGE_BG = "rgba(25,118,210,0.13)";
// Color used for the range-start marker circle
const RANGE_START_COLOR = "#f57c00";

const DateInputField = forwardRef(({
  label,
  value = "",
  min,
  max,
  onCommit,
  error,
  helperText,
  fullWidth = true,
  required = false,
  disabled = false,
  rangeStart,   // YYYY-MM-DD – the paired start date (shown as orange circle in end-date picker)
}, ref) => {
  const containerRef = useRef(null);
  const [anchor, setAnchor] = useState(null);

  const selectedDate = parseLocalDate(value);
  const minDate = parseLocalDate(min);
  const maxDate = parseLocalDate(max);
  const rangeStartDate = parseLocalDate(rangeStart);

  // A valid range exists when rangeStart is strictly before the selected date
  const hasRange = Boolean(rangeStartDate && selectedDate && rangeStartDate < selectedDate);

  const [viewYear, setViewYear] = useState(() => selectedDate?.getFullYear() ?? new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(() => selectedDate?.getMonth() ?? new Date().getMonth());

  const open = Boolean(anchor);

  useImperativeHandle(ref, () => ({
    open: (preferredDate) => {
      if (containerRef.current) {
        const base = preferredDate ? parseLocalDate(preferredDate) : (selectedDate || new Date());
        setViewYear(base?.getFullYear() ?? new Date().getFullYear());
        setViewMonth(base?.getMonth() ?? new Date().getMonth());
        setAnchor(containerRef.current);
      }
    },
  }));

  const handleOpen = (e) => {
    if (disabled) return;
    const base = selectedDate || (minDate ? minDate : new Date());
    setViewYear(base.getFullYear());
    setViewMonth(base.getMonth());
    setAnchor(e.currentTarget);
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1); }
    else setViewMonth((m) => m - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1); }
    else setViewMonth((m) => m + 1);
  };

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDayOfWeek = new Date(viewYear, viewMonth, 1).getDay();

  const isSelected = (day) =>
    selectedDate &&
    selectedDate.getFullYear() === viewYear &&
    selectedDate.getMonth() === viewMonth &&
    selectedDate.getDate() === day;

  const isDisabled = (day) => {
    const d = new Date(viewYear, viewMonth, day);
    if (minDate && d < minDate) return true;
    if (maxDate && d > maxDate) return true;
    return false;
  };

  const isToday = (day) => {
    const t = new Date();
    return t.getFullYear() === viewYear && t.getMonth() === viewMonth && t.getDate() === day;
  };

  const isRangeStartDay = (day) =>
    Boolean(
      rangeStartDate &&
      rangeStartDate.getFullYear() === viewYear &&
      rangeStartDate.getMonth() === viewMonth &&
      rangeStartDate.getDate() === day
    );

  const isInRange = (day) => {
    if (!hasRange) return false;
    const d = new Date(viewYear, viewMonth, day);
    return d > rangeStartDate && d < selectedDate;
  };

  // Returns the background for the outer (full-width) cell — creates the range strip
  const getCellBackground = (day) => {
    // Range-start boundary: right half colored
    if (hasRange && isRangeStartDay(day))
      return `linear-gradient(to right, transparent 50%, ${RANGE_BG} 50%)`;
    // Range-end boundary (selected): left half colored
    if (hasRange && isSelected(day))
      return `linear-gradient(to right, ${RANGE_BG} 50%, transparent 50%)`;
    // Middle of range: fully colored strip
    if (isInRange(day))
      return RANGE_BG;
    // If rangeStart exists but no selected yet — still show it with right-half tint
    if (!hasRange && isRangeStartDay(day) && rangeStartDate)
      return "transparent";
    return "transparent";
  };

  const handleDay = (day) => {
    if (isDisabled(day)) return;
    onCommit(toDateString(new Date(viewYear, viewMonth, day)));
    setAnchor(null);
  };

  const cells = [
    ...Array(firstDayOfWeek).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  const rows = [];
  for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));

  return (
    <Box ref={containerRef}>
      <TextField
        label={label}
        fullWidth={fullWidth}
        required={required}
        disabled={disabled}
        error={Boolean(error)}
        helperText={helperText}
        value={toDisplayString(selectedDate)}
        InputLabelProps={{ shrink: Boolean(selectedDate) }}
        onClick={handleOpen}
        inputProps={{ readOnly: true, style: { cursor: disabled ? "not-allowed" : "pointer" } }}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <CalendarToday fontSize="small" sx={{ color: "text.secondary", cursor: disabled ? "not-allowed" : "pointer" }} />
            </InputAdornment>
          ),
        }}
        sx={{ "& .MuiOutlinedInput-root": { cursor: disabled ? "not-allowed" : "pointer" } }}
      />

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { mt: 0.5, borderRadius: 2, boxShadow: 3 } }}
      >
        <Box sx={{ p: 2, width: 280, userSelect: "none" }}>
          {/* Month / Year header */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
            <IconButton size="small" onClick={prevMonth}>
              <ChevronLeft fontSize="small" />
            </IconButton>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {MONTHS[viewMonth]} {viewYear}
            </Typography>
            <IconButton size="small" onClick={nextMonth}>
              <ChevronRight fontSize="small" />
            </IconButton>
          </Box>

          {/* Day-of-week labels */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", mb: 0.5 }}>
            {DAY_LABELS.map((d) => (
              <Typography
                key={d}
                variant="caption"
                align="center"
                sx={{ fontWeight: 600, color: "text.secondary", py: 0.25 }}
              >
                {d}
              </Typography>
            ))}
          </Box>

          {/* Day grid */}
          {rows.map((row, ri) => (
            <Box key={ri} sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
              {row.map((day, ci) =>
                day == null ? (
                  <Box key={`e-${ci}`} />
                ) : (
                  /* Outer wrapper — full cell width, carries the range strip background */
                  <Box
                    key={day}
                    onClick={() => handleDay(day)}
                    sx={{
                      height: 34,
                      my: 0.25,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: isDisabled(day) ? "default" : "pointer",
                      background: getCellBackground(day),
                    }}
                  >
                    {/* Inner circle — the actual date indicator */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        flexShrink: 0,
                        bgcolor: isSelected(day)
                          ? "primary.main"
                          : isRangeStartDay(day)
                          ? RANGE_START_COLOR
                          : "transparent",
                        color: isSelected(day)
                          ? "primary.contrastText"
                          : isRangeStartDay(day)
                          ? "#fff"
                          : isDisabled(day)
                          ? "text.disabled"
                          : isToday(day)
                          ? "primary.main"
                          : "text.primary",
                        fontWeight: isToday(day) && !isSelected(day) && !isRangeStartDay(day) ? 700 : 400,
                        border:
                          isToday(day) && !isSelected(day) && !isRangeStartDay(day)
                            ? "1.5px solid"
                            : "none",
                        borderColor: "primary.main",
                        transition: "background-color 0.15s",
                        "&:hover": {
                          bgcolor:
                            !isDisabled(day) && !isSelected(day) && !isRangeStartDay(day)
                              ? "action.hover"
                              : undefined,
                        },
                      }}
                    >
                      <Typography variant="body2" sx={{ fontSize: "0.8rem", lineHeight: 1 }}>
                        {day}
                      </Typography>
                    </Box>
                  </Box>
                )
              )}
            </Box>
          ))}

          {/* Legend shown when a rangeStart is provided */}
          {rangeStart && (
            <Box sx={{ display: "flex", gap: 2, mt: 1.5, pt: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: RANGE_START_COLOR }} />
                <Typography variant="caption" color="text.secondary">Start</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "primary.main" }} />
                <Typography variant="caption" color="text.secondary">End</Typography>
              </Box>
              {hasRange && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: RANGE_BG, border: "1px solid rgba(25,118,210,0.3)" }} />
                  <Typography variant="caption" color="text.secondary">Range</Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Popover>
    </Box>
  );
});

export default DateInputField;
